import { useEffect, useCallback, useRef } from 'react';

export type TimeoutHook = () => [
  /**
   * Start a timeout. Clear the last timeout set from the same hook if any.
   * Same as setTimeout. Don't call this during server side rendering (i.e.,
   * outside of callback or useEffect)!
   */
  (handler: Function, timeout?: number) => void,
  /**
   * Clear the last timer set by the same hook.
   */
  () => void,
];

/**
 * Hook to returns setTimeout function that automatically clear
 * when the component unmount.
 */
const useTimeout: TimeoutHook = () => {
  const idRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (idRef.current) {
      clearTimeout(idRef.current);
      idRef.current = null;
    }
  }, [idRef]);

  const safeSetTimeout = useCallback(
    (handler: Function, timeout?: number) => {
      clear();
      const timerId = setTimeout(handler, timeout);
      idRef.current = timerId;
    },
    [idRef, clear],
  );

  useEffect(() => {
    return clear;
  }, [clear]);

  return [safeSetTimeout, clear];
};

export default useTimeout;
